/**
 * Gemini 2.5 Flash prompt + schema for VH2 video metadata extraction.
 * Shared by the optional local analyze server and the main API backend.
 */

export const CMS_GENRES = [
  "Sports",
  "Reality",
  "Family",
  "Mystery",
  "Thriller",
  "Romance",
  "Drama",
  "Animation",
  "Crime",
  "Documentary",
  "sci-fi",
  "Live",
  "Action",
  "Adventure",
  "Children",
  "Horror",
  "Comedy",
];

/** Matches Google AI Studio quickstart (`gemini-flash-latest`). Override via GEMINI_MODEL env on the server. */
export const GEMINI_MODEL = "gemini-flash-latest";

export const GEMINI_METADATA_PROMPT = `You are a metadata analyst for a streaming video CMS (VH2 Television).

Watch the provided video carefully (visuals + audio/dialogue) and return structured metadata for the Add Content form.

The video you receive is a MONTAGE: short clips sampled evenly across the whole
runtime, each labelled on screen with its real source timestamp. It is not continuous,
so judge the film as a whole from the samples and do not describe them as scenes in
sequence. Audio is included — use the dialogue, language and tone you can hear.

Rules:
- Only use genres from this list: ${CMS_GENRES.join(", ")}
- tagline: one marketing line, max 120 characters, no spoilers
- description: 2-3 short paragraphs, engaging teaser tone, no spoilers
- subgenre: one more specific label under the primary genre (e.g. "Legal Drama",
  "Coming-of-age"), or null when nothing specific fits
- mood: one or two words (e.g. "Reflective", "High-energy", "Atmospheric")
- contentWarnings: pick from common labels (Mild Language, Violence, Nudity, Drug Use, etc.) only when clearly present
- ageRating: US-style (G, PG, PG-13, R, NC-17) — use lower confidence if unsure
- language: ISO 639-1 code + English name of the primary spoken language
- year: best-guess release/production year as a 4-digit string
- tags: 5-10 short lowercase browse labels a viewer would filter by (themes, setting,
  tone, audience) — NOT the genres, which are returned separately
- keywords: 8-15 lowercase search terms someone might type to find this title,
  including subjects, places and notable concepts actually present
- seoDescription: a single plain sentence under 160 characters for a search result
- Every field must include confidence 0.0–1.0. Use confidence < 0.6 when guessing.
- If you cannot determine a field, set value to null and confidence to 0.

Return ONLY valid JSON matching this schema (no markdown):
{
  "title": { "value": string|null, "confidence": number },
  "tagline": { "value": string|null, "confidence": number },
  "description": { "value": string|null, "confidence": number },
  "genres": { "value": string[]|null, "confidence": number },
  "subgenre": { "value": string|null, "confidence": number },
  "mood": { "value": string|null, "confidence": number },
  "contentWarnings": { "value": string[]|null, "confidence": number },
  "language": { "value": { "code": string, "name": string }|null, "confidence": number },
  "ageRating": { "value": string|null, "confidence": number },
  "year": { "value": string|null, "confidence": number },
  "tags": { "value": string[]|null, "confidence": number },
  "keywords": { "value": string[]|null, "confidence": number },
  "seoDescription": { "value": string|null, "confidence": number }
}`;

export default GEMINI_METADATA_PROMPT;
